import PropTypes from 'prop-types';
import React from 'react';
import {injectIntl} from 'react-intl';
import CollapseSelect from 'components/CollapseSelect';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import findLastIndex from 'lodash/findLastIndex';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import {isAndroid} from 'utilities/userAgent';
import {format, subtractDays} from 'utilities/date';
import {
  RANGE_INTERVALS,
  RANGE_OPTIONS,
  RANGE_TYPES,
  RANGE_GROUPS,
  RANGE_DEFAULT_COUNT,
  RANGE_DEFAULT_INTERVAL,
} from './ChartDateRangeConstants';
import './ChartDateRange.css';

const rangeOptionsByGroup = groupBy(RANGE_OPTIONS, opt => opt.group.key);
const dateTimeRanges = RANGE_OPTIONS.map(c => c.dateTimeRange);

const getMinimalDateTimeRange = dateTimeRange => {
  if (!dateTimeRange || !dateTimeRange.dateTimeRangeType) return dateTimeRange;

  switch (dateTimeRange.dateTimeRangeType) {
    case 'INTERVAL_OFFSET':
      return pick(dateTimeRange, ['dateTimeRangeType', 'interval', 'offset']);
    case 'PERIOD_TO_DATE':
      return pick(dateTimeRange, ['dateTimeRangeType', 'interval']);
    case 'THIS_QUARTER_OFFSET_YEAR':
      return pick(dateTimeRange, ['dateTimeRangeType', 'offset']);
    case 'ROLLING_PERIOD':
      return pick(dateTimeRange, [
        'dateTimeRangeType',
        'interval',
        'count',
        'offset',
      ]);
    case 'FIXED_RANGE':
      return pick(dateTimeRange, ['dateTimeRangeType']);
    default:
      return dateTimeRange;
  }
};

export const lookupChoice = range => {
  if (Object.hasOwn(range, 'index')) {
    return range.index;
  }
  const fullMatchIndex = findLastIndex(dateTimeRanges, r => {
    const dateTimeRange = getMinimalDateTimeRange(range.dateTimeRange);
    return isEqual(dateTimeRange, r);
  });
  if (fullMatchIndex !== -1) {
    return fullMatchIndex;
  }

  const rangeType = pick(range.dateTimeRange, 'dateTimeRangeType');
  return findLastIndex(dateTimeRanges, rangeType);
};

export const buildDateRangeFilter = (
  {column, dateRangeFilter},
  {range, count, interval, beginDate, endDate}
) => {
  if (range.dateTimeRange === null) return null;

  const nextDateRangeFilter = {
    column: {
      exprType: 'COLUMN',
      column,
    },
    ...dateRangeFilter,
    dateTimeRange: {
      ...range.dateTimeRange,
    },
  };

  if (range.type === RANGE_TYPES.INTERVAL) {
    nextDateRangeFilter.dateTimeRange.count = count || RANGE_DEFAULT_COUNT;
    nextDateRangeFilter.dateTimeRange.interval =
      interval || RANGE_DEFAULT_INTERVAL;
  } else if (range.type === RANGE_TYPES.FIXED) {
    nextDateRangeFilter.dateTimeRange.beginDate = beginDate;
    nextDateRangeFilter.dateTimeRange.endDate = endDate;
  }

  return nextDateRangeFilter;
};

class ChartDateRange extends React.Component {
  constructor(props) {
    super(...arguments);
    const dateRangeFilter = props.dateRangeFilter || {};
    const dateTimeRange = dateRangeFilter.dateTimeRange || {};
    const today = format(new Date());
    const yesterday = format(subtractDays(new Date(), 1));

    this.state = {
      count: dateTimeRange.count || RANGE_DEFAULT_COUNT,
      interval: dateTimeRange.interval || RANGE_DEFAULT_INTERVAL,
      beginDate: dateTimeRange.beginDate || yesterday,
      endDate: dateTimeRange.endDate || today,
      range: RANGE_OPTIONS[lookupChoice(dateRangeFilter)],
    };
  }

  publishDateRangeFilter() {
    const nextDateRangeFilter = buildDateRangeFilter(this.props, this.state);
    this.props.onRangeChange(nextDateRangeFilter);
  }

  // Android doesn't trigger blur on date dialog close
  // https://code.google.com/p/chromium/issues/detail?id=303971
  publishIfAndroid() {
    if (isAndroid) {
      this.publishDateRangeFilter();
    }
  }

  handleRangeChange = e => {
    this.setState(
      {range: RANGE_OPTIONS[e.target.value]},
      this.publishDateRangeFilter
    );
  };

  handleCountBlur = () => {
    this.publishDateRangeFilter();
  };

  handleCountChange = e => {
    this.setState({count: e.target.value});
  };

  handleIntervalChange = e => {
    this.setState({interval: e.target.value}, this.publishDateRangeFilter);
  };

  handleBeginDateChange = e => {
    this.setState({beginDate: e.target.value}, this.publishIfAndroid);
  };

  handleEndDateChange = e => {
    this.setState({endDate: e.target.value}, this.publishIfAndroid);
  };

  handleBeginDateBlur = () => {
    this.publishDateRangeFilter();
  };

  handleEndDateBlur = () => {
    this.publishDateRangeFilter();
  };

  renderRangeSelect() {
    const value = lookupChoice(this.state.range);
    const choiceToOption = choice => {
      const label = this.props.intl.formatMessage(choice.label);
      return (
        <option key={choice.label.id} value={choice.index}>
          {label}
        </option>
      );
    };
    const groups = map(rangeOptionsByGroup, (choices, groupKey) => {
      const group = RANGE_GROUPS[groupKey];
      const label = this.props.intl.formatMessage(group);
      return (
        <optgroup label={label} key={group.id}>
          {choices.map(choiceToOption)}
        </optgroup>
      );
    });

    return (
      <CollapseSelect
        className="ChartDateRange__Field"
        onChange={this.handleRangeChange}
        value={value}
      >
        {groups}
      </CollapseSelect>
    );
  }

  renderCountInput = () => (
    <input
      className="ChartDateRange__Field ChartDateRange__Field--count"
      value={this.state.count}
      onBlur={this.handleCountBlur}
      onChange={this.handleCountChange}
    />
  );

  renderIntervalSelect = () => (
    <CollapseSelect
      className="ChartDateRange__Field"
      value={this.state.interval}
      onChange={this.handleIntervalChange}
    >
      {RANGE_INTERVALS.map(interval => (
        <option key={interval.key} value={interval.key}>
          {this.props.intl.formatMessage(interval.label)}
        </option>
      ))}
    </CollapseSelect>
  );

  renderFixedRanges = () => [
    <input
      key="beginDate"
      className="ChartDateRange__Field ChartDateRange__Field--date"
      type="date"
      value={this.state.beginDate}
      max={this.state.endDate}
      onChange={this.handleBeginDateChange}
      onBlur={this.handleBeginDateBlur}
    />,
    <input
      key="endDate"
      className="ChartDateRange__Field ChartDateRange__Field--date"
      type="date"
      value={this.state.endDate}
      min={this.state.beginDate}
      onChange={this.handleEndDateChange}
      onBlur={this.handleEndDateBlur}
    />,
  ];

  render() {
    const range = this.state.range;

    return (
      <div className="ChartDateRange">
        {this.renderRangeSelect()}
        {range.type === RANGE_TYPES.INTERVAL ? this.renderCountInput() : null}
        {range.type === RANGE_TYPES.INTERVAL
          ? this.renderIntervalSelect()
          : null}
        {range.type === RANGE_TYPES.FIXED ? this.renderFixedRanges() : null}
      </div>
    );
  }
}

ChartDateRange.propTypes = {
  dateRangeFilter: PropTypes.object,
  column: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
};

export default injectIntl(ChartDateRange);
